import { modeLabel, modeBlurb, MODE_CP, isExternalOwner } from "@/lib/businessModel";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, ShoppingCart, CheckCircle2, ArrowLeft, ShieldCheck, Rocket, FileText, Printer, Smartphone, ChevronLeft, ChevronRight, Headphones, Trash2, Plus, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import adminApi from "@/lib/adminApi";
import { openWarrantyWindow } from "@/components/WarrantyContractsTab";
import { CreateDialog as LayawayCreateDialog } from "@/components/LayawayTab";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ sale_date: today(), payment_method: "Efectivo", customer_name: "", customer_phone: "", customer_doc: "", notes: "" });
const PAYMENT_METHODS = ["Efectivo", "Transferencia", "Tarjeta", "Zelle", "USDT", "Mixto", "Otro"];
const MIXTO_METHODS = ["Efectivo", "Transferencia", "Tarjeta", "Zelle", "USDT", "Otro"];
const PAGE_SIZE = 18;

const modelSlug = (m) => String(m || "").replace(/\s+/g, "-").toLowerCase();
const genOf = (m) => { const x = String(m || "").match(/iPhone\s+(\d+)/i); return x ? Number(x[1]) : 0; };
const imeiShort = (imei) => (imei ? `IMEI …${String(imei).slice(-4)}` : null);

// Miniatura reutilizando la MISMA imagen del catálogo de Precios (por modelo).
const PhoneThumb = ({ model, color, className = "h-11 w-11" }) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [stage, setStage] = useState(0);
  const s = modelSlug(model);
  const src = stage === 0 ? `${base}/api/model-image/${encodeURIComponent(model)}${color ? `?color=${encodeURIComponent(color)}` : ""}` : stage === 1 ? `/phones/${s}.jpg` : null;
  return (
    <div className={`${className} shrink-0 rounded-lg border border-zinc-200 bg-white grid place-items-center overflow-hidden`}>
      {src ? <img src={src} alt={model} className="h-full w-full object-contain" onError={() => setStage((x) => x + 1)} />
        : <Smartphone className="h-5 w-5 text-zinc-300" />}
    </div>
  );
};

// Lista de ACCESORIOS para agregar al carrito.
const AccList = ({ items, loading, onPick, inCart }) => {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  if (!items.length) return <p className="text-sm text-zinc-400 py-10 text-center" data-testid="ss-acc-empty">No hay accesorios disponibles.</p>;
  return (
    <>
      <p className="text-xs text-zinc-400" data-testid="ss-acc-count">{items.length} producto(s) disponible(s)</p>
      <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-y-auto bg-white max-h-[min(55vh,460px)]" data-testid="ss-acc-list">
        {items.map((a) => (
          <div key={a.id} data-testid={`ss-acc-item-${a.id}`} className="flex items-center gap-3 px-3 py-2.5">
            <div className="h-11 w-11 shrink-0 rounded-lg border border-zinc-200 bg-white grid place-items-center overflow-hidden">
              {a.photo ? <img src={a.photo} alt={a.name} className="h-full w-full object-cover" /> : <Headphones className="h-5 w-5 text-zinc-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-zinc-900 truncate">{a.name}</p>
              <p className="text-xs text-zinc-500 truncate">{[a.category, a.sku, `disp. ${a.available}`].filter(Boolean).join(" · ")}</p>
            </div>
            <span className="text-sm font-semibold text-zinc-900 shrink-0 text-right">{a.sale_price_unit == null ? "—" : `$${Number(a.sale_price_unit).toFixed(0)}`}</span>
            <Button data-testid={`ss-acc-select-${a.id}`} size="sm" disabled={inCart(`accessory:${a.id}`)} onClick={() => onPick(a)} className="h-9 rounded-xl bg-zinc-900 hover:bg-zinc-800 shrink-0">{inCart(`accessory:${a.id}`) ? "En carrito" : "Agregar"}</Button>
          </div>
        ))}
      </div>
    </>
  );
};

// Puntaje de búsqueda con prioridad de serie para números cortos e IMEI para 7+ dígitos.
const scoreItem = (it, term) => {
  if (!term) return 1;
  const t = term.toLowerCase();
  const model = String(it.model || "").toLowerCase();
  const cap = String(it.capacity || "").toLowerCase();
  const color = String(it.color || "").toLowerCase();
  const sim = String(it.sim_type || "").toLowerCase();
  const id = String(it.id || "").toLowerCase();
  const imei = String(it.imei || "").toLowerCase();
  const gen = String(genOf(it.model));
  if (/^\d+$/.test(t)) {
    if (t.length >= 7) { if (imei.includes(t)) return 100; if (id.includes(t)) return 90; return 0; }
    if (t.length <= 2) { if (gen === t) return 100; if (gen.startsWith(t)) return 85; return 0; }
    if (cap.includes(t)) return 80; if (model.includes(t)) return 60; if (imei.includes(t)) return 45; if (id.includes(t)) return 45; return 0;
  }
  if (model.includes(t)) return 100; if (color.includes(t)) return 60; if (sim.includes(t)) return 50;
  if (cap.includes(t)) return 45; if (id.includes(t)) return 40; if (imei.includes(t)) return 40; return 0;
};

// VENTA SIMPLE — carrito multi-item (teléfonos usados/nuevos + accesorios) en UNA sola venta (1 VEN).
export const SimpleSaleTab = () => {
  const [step, setStep] = useState(1);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [list, setList] = useState(null);
  const [newList, setNewList] = useState(null);
  const [accList, setAccList] = useState(null);
  const [accErr, setAccErr] = useState(null);
  const [source, setSource] = useState("used"); // "used" | "new" | "accessory"
  const [priceMap, setPriceMap] = useState({});
  const [filterGen, setFilterGen] = useState("all");
  const [page, setPage] = useState(1);
  const [focused, setFocused] = useState(false);
  const [cart, setCart] = useState([]); // líneas del carrito
  const [form, setForm] = useState(emptyForm());
  const [breakdown, setBreakdown] = useState([{ method: "Efectivo", amount: "" }]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // comprobante
  const [apartarOpen, setApartarOpen] = useState(false); // diálogo de Apartado reutilizado
  // Garantía en línea (por teléfono del comprobante)
  const [warFor, setWarFor] = useState(null); // línea de teléfono seleccionada
  const [warMonths, setWarMonths] = useState("3");
  const [warCust, setWarCust] = useState({ name: "", phone: "", email: "" });
  const [warNotes, setWarNotes] = useState("");
  const [warIcloud, setWarIcloud] = useState({ created: false, email: "", password: "" });
  const [warSaving, setWarSaving] = useState(false);
  const [warDone, setWarDone] = useState({}); // { [lineId]: warranty }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => {
    setList(null);
    adminApi.get("/admin/inventory", { params: { status: "DISPONIBLE" } })
      .then((r) => setList(r.data))
      .catch(() => { setList([]); toast.error("No se pudo cargar el inventario disponible"); });
  };
  const loadNew = () => {
    setNewList(null);
    adminApi.get("/admin/new-iphones", { params: { status: "DISPONIBLE" } })
      .then((r) => setNewList(r.data)).catch(() => { setNewList([]); });
  };
  const loadAcc = () => {
    setAccList(null); setAccErr(null);
    adminApi.get("/admin/accessories")
      .then((r) => setAccList((r.data || []).filter((a) => a.active !== false && a.available > 0)))
      .catch((e) => { setAccErr(e?.response?.data?.detail || "No se pudieron cargar los accesorios. Intenta de nuevo."); setAccList([]); });
  };
  useEffect(() => { load(); loadNew(); loadAcc(); }, []);
  useEffect(() => { setPage(1); setQ(""); setDq(""); setFilterGen("all"); }, [source]);
  useEffect(() => {
    adminApi.get("/admin/price-reference").then((r) => {
      const m = {}; (r.data || []).forEach((row) => { m[`${row.model}__${row.capacity}`] = row.market_high; }); setPriceMap(m);
    }).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [dq, filterGen]);

  const priceFor = (it) => priceMap[`${it.model}__${it.capacity}`];
  const isAcc = source === "accessory";
  const activeList = source === "used" ? list : newList;
  const priceOf = (it) => {
    if (it == null) return null;
    if (source !== "used") return it.sale_price ?? null;
    return (it.price_mode === "manual" && it.list_price != null) ? it.list_price : priceFor(it);
  };
  const accTerm = dq.trim().toLowerCase();
  const accResults = (accList || []).filter((a) => !accTerm || [a.name, a.sku, a.category].some((f) => f && String(f).toLowerCase().includes(accTerm)));

  const gens = [...new Set((activeList || []).map((it) => genOf(it.model)))].filter((n) => n > 0).sort((a, b) => b - a);
  const genFiltered = (activeList || []).filter((it) => filterGen === "all" || String(genOf(it.model)) === filterGen);
  const term = dq.trim();
  const results = genFiltered
    .map((it) => ({ it, s: scoreItem(it, term) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || genOf(b.it.model) - genOf(a.it.model) || String(a.it.model).localeCompare(String(b.it.model)))
    .map((x) => x.it);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const pageItems = results.slice((pageClamped - 1) * PAGE_SIZE, pageClamped * PAGE_SIZE);
  const showDropdown = focused && term !== "" && results.length > 0;
  const dropItems = results.slice(0, 8);

  // ---------------- CARRITO ----------------
  const inCart = (key) => cart.some((l) => l.key === key);
  const cartTotal = cart.reduce((t, l) => t + (Number(l.unit_price || 0) * Number(l.qty || 1)), 0);

  const addDevice = (it) => {
    const key = `${source}:${it.id}`;
    if (inCart(key)) { toast.info("Este equipo ya está en el carrito"); return; }
    const sp = priceOf(it);
    setCart((c) => [...c, {
      key, source, id: it.id, is_acc: false,
      model: it.model, capacity: it.capacity, color: it.color, imei: it.imei,
      owner_name: it.owner_name, owner_code: it.owner_code, settlement_mode: it.settlement_mode, profit_share_pct: it.profit_share_pct,
      label: `${it.model} ${it.capacity || ""}`.trim(),
      reference_price: sp != null ? Number(sp) : null,
      unit_price: sp != null ? String(sp) : "", qty: 1, available: 1,
    }]);
    toast.success("Agregado al carrito");
    setFocused(false);
  };
  const addAccessory = (a) => {
    const key = `accessory:${a.id}`;
    if (inCart(key)) { toast.info("Este accesorio ya está en el carrito; ajusta la cantidad"); return; }
    setCart((c) => [...c, {
      key, source: "accessory", id: a.id, is_acc: true, photo: a.photo,
      label: a.name, sku: a.sku, reference_price: a.sale_price_unit != null ? Number(a.sale_price_unit) : null,
      unit_price: a.sale_price_unit != null ? String(a.sale_price_unit) : "",
      qty: 1, available: a.available,
    }]);
    toast.success("Accesorio agregado");
  };
  const removeLine = (key) => setCart((c) => c.filter((l) => l.key !== key));
  const setLine = (key, patch) => setCart((c) => c.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const cartValid = cart.length > 0 && cart.every((l) => l.unit_price !== "" && Number(l.unit_price) >= 0 && (!l.is_acc || (Number(l.qty) >= 1 && Number(l.qty) <= l.available)));

  // Pago mixto: desglose por método, validado contra el total de la venta.
  const isMixto = form.payment_method === "Mixto";
  const paidTotal = Math.round(breakdown.reduce((t, b) => t + (Number(b.amount) || 0), 0) * 100) / 100;
  const mixtoRemaining = Math.round((cartTotal - paidTotal) * 100) / 100;
  const mixtoValid = !isMixto || (breakdown.every((b) => b.amount !== "" && Number(b.amount) >= 0) && Math.abs(mixtoRemaining) < 0.01);
  const setBdLine = (i, patch) => setBreakdown((b) => b.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addBdLine = () => setBreakdown((b) => [...b, { method: "Efectivo", amount: "" }]);
  const removeBdLine = (i) => setBreakdown((b) => (b.length > 1 ? b.filter((_, idx) => idx !== i) : b));

  const resetAll = () => {
    setStep(1); setCart([]); setForm(emptyForm()); setResult(null); setBreakdown([{ method: "Efectivo", amount: "" }]);
    setWarFor(null); setWarDone({}); setWarMonths("3"); setWarCust({ name: "", phone: "", email: "" }); setWarNotes("");
    setQ(""); setDq(""); setFilterGen("all"); setPage(1); setSource("used"); load(); loadNew(); loadAcc();
  };

  const goCheckout = () => {
    if (!cartValid) { toast.error("Revisa precios y cantidades del carrito"); return; }
    setStep(2); window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  // Apartar/Reservar: solo cuando hay EXACTAMENTE un iPhone (usado o nuevo) en el carrito.
  const singlePhoneLine = cart.length === 1 && !cart[0].is_acc ? cart[0] : null;
  const buildApartarItem = (l) => ({
    id: l.id, source: l.source === "new" ? "NEW" : "USED",
    condition: l.source === "new" ? "NUEVO" : "USADO",
    model: l.model, capacity: l.capacity, color: l.color, imei: l.imei,
  });

  const finalizeSale = async () => {
    if (!cartValid) { toast.error("Revisa precios y cantidades del carrito"); return; }
    if (isMixto && !mixtoValid) {
      toast.error(mixtoRemaining > 0 ? `Faltan ${money(mixtoRemaining)} por asignar.` : `Excede el total por ${money(Math.abs(mixtoRemaining))}.`);
      return;
    }
    setSaving(true);
    try {
      const items = cart.map((l) => ({ source: l.source, id: l.id, sale_price: Number(l.unit_price), qty: l.is_acc ? Number(l.qty) : 1, reference_price: l.reference_price != null ? Number(l.reference_price) : null }));
      const { data } = await adminApi.post("/admin/sales/checkout", {
        items, sale_date: form.sale_date || null, payment_method: form.payment_method || null,
        payment_breakdown: isMixto ? breakdown.map((b) => ({ method: b.method, amount: Number(b.amount) })) : null,
        customer_name: form.customer_name || null, customer_phone: form.customer_phone || null,
        customer_doc: form.customer_doc || null, notes: form.notes || null,
      });
      setResult(data);
      setWarCust({ name: form.customer_name || "", phone: form.customer_phone || "", email: "" });
      setStep(3);
      window.scrollTo?.({ top: 0, behavior: "smooth" });
      toast.success(`Venta registrada · ${data?.sale_reference || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la venta. Nada fue vendido.");
    } finally { setSaving(false); }
  };

  const openWarranty = (line) => {
    setWarFor(line);
    setWarMonths("3"); setWarNotes("");
    setWarIcloud({ created: false, email: "", password: "" });
    setWarCust({ name: form.customer_name || result?.receipt?.customer_name || "", phone: form.customer_phone || result?.receipt?.customer_phone || "", email: "" });
  };
  const createWarranty = async () => {
    if (!warFor) return;
    if (!warCust.name.trim()) { toast.error("El nombre del cliente es obligatorio para la garantía"); return; }
    if (warIcloud.created && (!warIcloud.email.trim() || !warIcloud.password.trim())) { toast.error("Correo y contraseña de iCloud son obligatorios"); return; }
    setWarSaving(true);
    try {
      const { data } = await adminApi.post("/admin/warranties", {
        inventory_id: warFor.id, imei: warFor.imei, months: Number(warMonths), notes: warNotes || null,
        customer: { name: warCust.name.trim(), phone: warCust.phone || null, email: warCust.email || null },
        icloud_created: warIcloud.created,
        icloud_email: warIcloud.created ? warIcloud.email.trim() : null,
        icloud_password: warIcloud.created ? warIcloud.password : null,
      });
      setWarDone((w) => ({ ...w, [warFor.id]: data }));
      setWarFor(null);
      toast.success(`Garantía ${data.warranty_number} lista`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la garantía");
    } finally { setWarSaving(false); }
  };

  const StepBadge = ({ n, label, active, done }) => (
    <div className="flex items-center gap-2" data-testid={`ss-step-${n}`}>
      <span className={`grid h-7 w-7 place-items-center rounded-full text-xs font-bold ${done ? "bg-emerald-600 text-white" : active ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-500"}`}>{done ? "✓" : n}</span>
      <span className={`text-sm font-medium ${active || done ? "text-zinc-900" : "text-zinc-400"}`}>{label}</span>
    </div>
  );

  // ---------------- Panel de carrito reutilizable ----------------
  const CartPanel = ({ editable }) => (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="ss-cart">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Carrito <span className="text-zinc-400 font-normal">({cart.length})</span></p>
        <span className="text-sm font-bold text-zinc-900" data-testid="ss-cart-total">Total: {money(cartTotal)}</span>
      </div>
      {cart.length === 0 ? <p className="text-sm text-zinc-400 py-4 text-center" data-testid="ss-cart-empty">Agrega productos para iniciar la venta.</p> : (
        <div className="divide-y divide-zinc-100">
          {cart.map((l) => (
            <div key={l.key} data-testid={`ss-cart-line-${l.key}`} className="flex items-center gap-3 py-2.5">
              {l.is_acc
                ? <div className="h-10 w-10 shrink-0 rounded-lg border border-zinc-200 bg-white grid place-items-center overflow-hidden">{l.photo ? <img src={l.photo} alt={l.label} className="h-full w-full object-cover" /> : <Headphones className="h-4 w-4 text-zinc-300" />}</div>
                : <PhoneThumb model={l.model} color={l.color} className="h-10 w-10" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-zinc-900 truncate">{l.label}</p>
                <p className="text-xs text-zinc-500 truncate">{l.is_acc ? [l.sku, `disp. ${l.available}`].filter(Boolean).join(" · ") : imeiShort(l.imei) || l.id}</p>
                <p className="text-[11px] text-zinc-400" data-testid={`ss-cart-ref-${l.key}`}>Publicado: {l.reference_price != null ? money(l.reference_price) : "—"}</p>
                {!l.is_acc && isExternalOwner(l.owner_code) && (
                  <div className="mt-1" data-testid={`ss-cart-mode-${l.key}`}>
                    <span className="inline-block rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 text-[10px] font-semibold">👤 {l.owner_name} · {modeLabel(l.settlement_mode)}{l.settlement_mode === MODE_CP && l.profit_share_pct != null ? ` · ${l.profit_share_pct}%` : ""}</span>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{modeBlurb(l.settlement_mode)}</p>
                  </div>
                )}
              </div>
              {editable ? (
                <div className="flex items-center gap-2 shrink-0">
                  {l.is_acc && (
                    <Input data-testid={`ss-cart-qty-${l.key}`} type="number" min="1" max={l.available} value={l.qty}
                      onChange={(e) => setLine(l.key, { qty: e.target.value })} className="h-9 w-16 rounded-lg text-center" />
                  )}
                  <label className="flex flex-col items-end gap-0.5">
                    <span className="text-[9px] uppercase tracking-wide text-zinc-400">Precio final</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-zinc-400">$</span>
                      <Input data-testid={`ss-cart-price-${l.key}`} type="number" min="0" inputMode="decimal" value={l.unit_price}
                        onChange={(e) => setLine(l.key, { unit_price: e.target.value })} className="h-9 w-24 rounded-lg" />
                    </div>
                  </label>
                  <span className="text-sm font-semibold text-zinc-900 w-16 text-right">{money(Number(l.unit_price || 0) * Number(l.qty || 1))}</span>
                  <button onClick={() => removeLine(l.key)} data-testid={`ss-cart-remove-${l.key}`} className="text-rose-500 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-zinc-900">{money(Number(l.unit_price || 0) * Number(l.qty || 1))}</p>
                  <p className="text-[11px] text-zinc-400">{l.is_acc ? `${l.qty} × ${money(l.unit_price)}` : `Final ${money(l.unit_price)}`}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="simple-sale-panel" className="space-y-5">
      <div className="rounded-2xl border border-zinc-200 bg-white p-4">
        <p className="font-semibold text-zinc-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Venta simple</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <StepBadge n={1} label="Agregar productos" active={step === 1} done={step > 1} />
          <span className="hidden sm:block h-px w-8 bg-zinc-200" />
          <StepBadge n={2} label="Checkout" active={step === 2} done={step > 2} />
        </div>
      </div>

      {/* ---------------- PASO 1: BUSCADOR + CATÁLOGO + CARRITO ---------------- */}
      {step === 1 && (
        <div className="space-y-3" data-testid="ss-step1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ss-source-cards">
            {[["used", "iPhone usado", "Equipos usados disponibles", Smartphone],
              ["new", "iPhone nuevo", "Equipos nuevos / sellados", Rocket],
              ["accessory", "Accesorios", "Cables, AirPods, cargadores y más", Headphones]].map(([key, title, sub, Icon]) => {
              const on = source === key;
              return (
                <button key={key} data-testid={`ss-source-${key}`} onClick={() => setSource(key)}
                  className={`relative text-left rounded-2xl border p-4 min-h-[76px] transition-all ${on ? "bg-zinc-900 border-zinc-900 text-white shadow-md" : "bg-white border-zinc-200 text-zinc-900 hover:border-zinc-300 hover:shadow-sm"}`}>
                  {on && <span className="absolute top-3 right-3 grid h-5 w-5 place-items-center rounded-full bg-white text-zinc-900 text-[11px] font-bold">✓</span>}
                  <Icon className={`w-5 h-5 ${on ? "text-white" : "text-zinc-500"}`} />
                  <p className="mt-2 font-semibold">{title}</p>
                  <p className={`text-xs ${on ? "text-white/70" : "text-zinc-500"}`}>{sub}</p>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="ss-search" value={q} onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)}
              placeholder={isAcc ? "Buscar producto o SKU..." : "Buscar por modelo, capacidad, IMEI o ID..."} className="h-11 rounded-xl pl-9 bg-white" />
            {!isAcc && showDropdown && (
              <div className="absolute left-0 right-0 top-12 z-20 max-h-80 overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-lg" data-testid="ss-dropdown">
                {dropItems.map((it) => {
                  const p = priceOf(it);
                  return (
                    <button key={it.id} data-testid={`ss-drop-item-${it.id}`} onMouseDown={(e) => { e.preventDefault(); addDevice(it); }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50 border-b border-zinc-100 last:border-0">
                      <PhoneThumb model={it.model} color={it.color} className="h-10 w-10" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{it.model} <span className="font-normal text-zinc-400">{it.capacity}</span></p>
                        <p className="text-xs text-zinc-500 truncate">{[it.color, imeiShort(it.imei)].filter(Boolean).join(" · ") || "—"}</p>
                        {isExternalOwner(it.owner_code) && <p className="text-[11px] text-indigo-700 truncate">👤 {it.owner_name} · {modeLabel(it.settlement_mode)}</p>}
                      </div>
                      <span className="text-sm font-semibold text-zinc-900 shrink-0">{money(p)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {!isAcc && (<div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="ss-gen-filters">
            <button data-testid="ss-gen-all" onClick={() => setFilterGen("all")}
              className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${filterGen === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>Todos</button>
            {gens.map((g) => (
              <button key={g} data-testid={`ss-gen-${g}`} onClick={() => setFilterGen(String(g))}
                className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium transition-colors ${filterGen === String(g) ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{g}</button>
            ))}
          </div>)}

          {!isAcc && (!activeList ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
            : results.length === 0 ? <p className="text-sm text-zinc-400 py-10 text-center" data-testid="ss-empty">No hay equipos DISPONIBLES que coincidan.</p>
            : (
            <>
              <p className="text-xs text-zinc-400" data-testid="ss-result-count">{results.length} equipo(s) disponible(s)</p>
              <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-y-auto bg-white max-h-[min(55vh,460px)]" data-testid="ss-device-list">
                {results.map((it) => {
                  const p = priceOf(it);
                  const key = `${source}:${it.id}`;
                  return (
                    <div key={it.id} data-testid={`ss-item-${it.id}`} className="flex items-center gap-3 px-3 py-2.5">
                      <PhoneThumb model={it.model} color={it.color} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{it.model} <span className="font-normal text-zinc-400">{it.capacity}</span></p>
                        <p className="text-xs text-zinc-500 truncate">{[it.color, imeiShort(it.imei)].filter(Boolean).join(" · ") || "—"}</p>
                        {isExternalOwner(it.owner_code) && <p className="text-[11px] text-indigo-700 truncate" data-testid={`ss-mode-${it.id}`}>👤 {it.owner_name} · {modeLabel(it.settlement_mode)}{it.settlement_mode === MODE_CP && it.profit_share_pct != null ? ` · ${it.profit_share_pct}%` : ""}</p>}
                      </div>
                      <span className="text-sm font-semibold text-zinc-900 shrink-0 text-right" data-testid={`ss-price-${it.id}`}>{money(p)}</span>
                      <Button data-testid={`ss-select-${it.id}`} size="sm" disabled={inCart(key)} onClick={() => addDevice(it)} className="h-9 rounded-xl bg-zinc-900 hover:bg-zinc-800 shrink-0">{inCart(key) ? "En carrito" : "Agregar"}</Button>
                    </div>
                  );
                })}
              </div>
            </>
          ))}
          {isAcc && (accErr ? <p className="text-sm text-red-600 py-10 text-center" data-testid="ss-acc-error">{accErr}</p> : <AccList items={accResults} loading={accList === null} onPick={addAccessory} inCart={inCart} />)}

          {/* Carrito editable */}
          <CartPanel editable />
          <div className="space-y-2">
            {singlePhoneLine && (
              <Button variant="outline" data-testid="ss-apartar" onClick={() => setApartarOpen(true)}
                className="h-12 w-full rounded-2xl border-zinc-300 text-base font-semibold text-zinc-800 hover:bg-zinc-50">
                <Package className="w-5 h-5 mr-2" /> Apartar / Reservar equipo
              </Button>
            )}
            <Button data-testid="ss-go-checkout" onClick={goCheckout} disabled={!cartValid}
              className="h-14 w-full rounded-2xl bg-zinc-900 hover:bg-zinc-800 text-base font-semibold disabled:opacity-50">
              Continuar al cobro · {cart.length} artículo(s) · {money(cartTotal)}
            </Button>
          </div>

          {apartarOpen && singlePhoneLine && (
            <LayawayCreateDialog kind="APARTADO" isAdmin={false}
              preItem={buildApartarItem(singlePhoneLine)}
              prePrice={singlePhoneLine.unit_price || singlePhoneLine.reference_price || ""}
              onClose={() => setApartarOpen(false)}
              onDone={() => { setApartarOpen(false); resetAll(); }} />
          )}
        </div>
      )}

      {/* ---------------- PASO 2: CHECKOUT ---------------- */}
      {step === 2 && (
        <div className="space-y-4" data-testid="ss-step2">
          <button onClick={() => setStep(1)} data-testid="ss-add-more" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            <ArrowLeft className="w-4 h-4" /> Volver a productos
          </button>

          <CartPanel editable={false} />

          <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Detalles del pago</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-zinc-500">Fecha de venta</span>
                <Input data-testid="ss-date" type="date" value={form.sale_date} onChange={(e) => set("sale_date", e.target.value)} className="h-11 rounded-xl" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-zinc-500">Método de pago</span>
                <select data-testid="ss-payment" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm bg-white">
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            </div>
            {isMixto && (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-3 space-y-2" data-testid="ss-mixto-block">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Desglose del pago</p>
                {breakdown.map((b, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`ss-mixto-line-${i}`}>
                    <select data-testid={`ss-mixto-method-${i}`} value={b.method} onChange={(e) => setBdLine(i, { method: e.target.value })} className="h-10 flex-1 rounded-lg border border-zinc-200 px-2 text-sm bg-white">
                      {MIXTO_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-zinc-400">$</span>
                      <Input data-testid={`ss-mixto-amount-${i}`} type="number" min="0" inputMode="decimal" value={b.amount} onChange={(e) => setBdLine(i, { amount: e.target.value })} className="h-10 w-28 rounded-lg" />
                    </div>
                    {breakdown.length > 1 && <button onClick={() => removeBdLine(i)} data-testid={`ss-mixto-remove-${i}`} className="text-rose-500 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addBdLine} data-testid="ss-mixto-add" className="h-9 rounded-lg"><Plus className="w-4 h-4 mr-1" /> Agregar método</Button>
                <div className="pt-1 space-y-0.5 text-sm border-t border-zinc-200">
                  <div className="flex justify-between"><span className="text-zinc-500">Total venta</span><span className="font-semibold text-zinc-900" data-testid="ss-mixto-sale-total">{money(cartTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Total pagado</span><span className="font-semibold text-zinc-900" data-testid="ss-mixto-paid">{money(paidTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Restante</span><span className={`font-bold ${Math.abs(mixtoRemaining) < 0.01 ? "text-emerald-600" : "text-rose-600"}`} data-testid="ss-mixto-remaining">{money(mixtoRemaining)}</span></div>
                </div>
                {!mixtoValid && <p className="text-xs font-medium text-rose-600" data-testid="ss-mixto-warning">{mixtoRemaining > 0 ? `Faltan ${money(mixtoRemaining)} por asignar.` : `Excede el total por ${money(Math.abs(mixtoRemaining))}.`}</p>}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Datos del comprador</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="ss-customer-name" placeholder="Nombre del cliente" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="h-11 rounded-xl" />
              <Input data-testid="ss-customer-phone" placeholder="Teléfono" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="h-11 rounded-xl" />
              <Input data-testid="ss-customer-doc" placeholder="Documento / Cédula / RIF" value={form.customer_doc} onChange={(e) => set("customer_doc", e.target.value)} className="h-11 rounded-xl" />
              <Input data-testid="ss-notes" placeholder="Notas (opcional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="h-11 rounded-xl" />
            </div>
          </div>

          <Button data-testid="ss-finalize" onClick={finalizeSale} disabled={!cartValid || !mixtoValid || saving} className="h-14 w-full rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-base font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Rocket className="w-5 h-5 mr-2" /> Finalizar y Registrar Venta · {money(cartTotal)}</>}
          </Button>
        </div>
      )}

      {/* ---------------- PASO 3: COMPROBANTE ---------------- */}
      {step === 3 && result && (
        <div className="space-y-4" data-testid="ss-step3">
          <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/50 p-5 text-center">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-600 text-white"><CheckCircle2 className="h-6 w-6" /></span>
            <p className="mt-3 text-lg font-semibold text-emerald-800" data-testid="ss-success-title">Venta registrada correctamente</p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="ss-receipt">
            <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
              <div><p className="text-xs text-zinc-500">Comprobante</p><p className="text-base font-bold text-zinc-900 font-mono" data-testid="ss-receipt-id">{result.sale_reference}</p></div>
              <div className="text-right"><p className="text-xs text-zinc-500">Cliente</p><p className="text-sm font-medium text-zinc-900">{result.receipt?.customer_name || "—"}</p></div>
            </div>
            <div className="divide-y divide-zinc-100">
              {(result.lines || []).map((l, idx) => (
                <div key={idx} className="flex items-center justify-between py-2" data-testid={`ss-receipt-line-${idx}`}>
                  <div className="min-w-0"><p className="text-sm font-medium text-zinc-900 truncate">{l.label}{l.qty > 1 ? ` ×${l.qty}` : ""}</p><p className="text-[11px] text-zinc-400">{l.type === "ACCESORIO" ? (l.imei || "Accesorio") : (l.imei ? `IMEI ${l.imei}` : l.id)}</p></div>
                  <span className="text-sm font-semibold text-zinc-900 shrink-0">{money(l.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
              <span className="text-sm font-semibold text-zinc-700">TOTAL</span>
              <span className="text-lg font-bold text-zinc-900" data-testid="ss-receipt-total">{money(result.total)}</span>
            </div>
            {result.receipt?.payment_method && (
              <div className="mt-2 pt-2 border-t border-zinc-100 text-xs" data-testid="ss-receipt-payment">
                <div className="flex justify-between"><span className="text-zinc-500">Pago</span><span className="font-medium text-zinc-900">{result.receipt.payment_method}</span></div>
                {Array.isArray(result.receipt.payment_breakdown) && result.receipt.payment_breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-zinc-500" data-testid={`ss-receipt-pay-${i}`}><span className="pl-2">{b.method}</span><span>{money(b.amount)}</span></div>
                ))}
              </div>
            )}
          </div>

          {/* Garantías por teléfono */}
          {(result.lines || []).some((l) => l.type !== "ACCESORIO") && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-2" data-testid="ss-warranty-section">
              <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Garantías de equipos</p>
              {(result.lines || []).filter((l) => l.type !== "ACCESORIO").map((l) => {
                const done = warDone[l.id];
                return (
                  <div key={l.id} className="flex items-center justify-between gap-2 rounded-xl border border-zinc-100 p-2.5" data-testid={`ss-war-row-${l.id}`}>
                    <div className="min-w-0"><p className="text-sm font-medium text-zinc-900 truncate">{l.label}</p><p className="text-[11px] text-zinc-400">{l.imei ? `IMEI ${l.imei}` : l.id}</p></div>
                    {done ? (
                      <div className="flex gap-1 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => openWarrantyWindow(done.id, "letter", false)} className="h-9 rounded-lg" data-testid={`ss-war-view-${l.id}`}><FileText className="w-4 h-4" /></Button>
                        <Button size="sm" onClick={() => openWarrantyWindow(done.id, "thermal", true)} className="h-9 rounded-lg bg-zinc-900 hover:bg-zinc-800" data-testid={`ss-war-print-${l.id}`}><Printer className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => openWarranty(l)} className="h-9 rounded-lg shrink-0" data-testid={`ss-war-open-${l.id}`}><ShieldCheck className="w-4 h-4 mr-1" /> Garantía</Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {warFor && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="ss-warranty-form">
              <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Garantía · {warFor.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1"><span className="text-xs text-zinc-500">Meses de garantía *</span>
                  <select data-testid="ss-warranty-months" value={warMonths} onChange={(e) => setWarMonths(e.target.value)} className="h-11 rounded-xl border border-zinc-200 px-3 text-sm bg-white">{["1", "3", "6", "12"].map((m) => <option key={m} value={m}>{m} mes(es)</option>)}</select></label>
                <Input data-testid="ss-warranty-name" placeholder="Cliente *" value={warCust.name} onChange={(e) => setWarCust({ ...warCust, name: e.target.value })} className="h-11 rounded-xl" />
                <Input data-testid="ss-warranty-phone" placeholder="Teléfono" value={warCust.phone} onChange={(e) => setWarCust({ ...warCust, phone: e.target.value })} className="h-11 rounded-xl" />
                <Input data-testid="ss-warranty-email" placeholder="Correo (opcional)" value={warCust.email} onChange={(e) => setWarCust({ ...warCust, email: e.target.value })} className="h-11 rounded-xl" />
                <Input data-testid="ss-warranty-notes" placeholder="Observaciones (opcional)" value={warNotes} onChange={(e) => setWarNotes(e.target.value)} className="h-11 rounded-xl sm:col-span-2" />
              </div>
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3 space-y-2" data-testid="ss-warranty-icloud-block">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="ss-warranty-icloud-check" checked={warIcloud.created} onChange={(e) => setWarIcloud({ ...warIcloud, created: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                  <span className="text-sm font-medium text-zinc-800">Creamos cuenta de iCloud</span>
                </label>
                {warIcloud.created && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input data-testid="ss-warranty-icloud-email" placeholder="Correo de la cuenta *" value={warIcloud.email} onChange={(e) => setWarIcloud({ ...warIcloud, email: e.target.value })} className="h-10 rounded-xl" />
                    <Input data-testid="ss-warranty-icloud-password" type="text" placeholder="Contraseña *" value={warIcloud.password} onChange={(e) => setWarIcloud({ ...warIcloud, password: e.target.value })} className="h-10 rounded-xl" />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setWarFor(null)} className="h-11 rounded-xl" data-testid="ss-warranty-cancel">Cancelar</Button>
                <Button data-testid="ss-warranty-create" onClick={createWarranty} disabled={warSaving} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 flex-1">{warSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear garantía"}</Button>
              </div>
            </div>
          )}

          <Button data-testid="ss-restart" variant="outline" onClick={resetAll} className="h-12 w-full rounded-2xl border-zinc-200">Nueva venta</Button>
        </div>
      )}
    </div>
  );
};
